import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type { WhatsappTemplate } from '@/types/outreach';

export interface WhatsappSyncResult {
  synced: number;
  created: number;
  updated: number;
  errors: number;
  skipped: boolean;
}

const KEYS = {
  all: ['whatsapp', 'templates'] as const,
  list: () => [...KEYS.all, 'list'] as const,
};

export const useWhatsappTemplates = () => {
  return useQuery<WhatsappTemplate[]>({
    queryKey: KEYS.list(),
    queryFn: async () => {
      const { data } = await api.get(API_ROUTES.whatsappTemplates);
      return data.results ?? data;
    },
  });
};

export const useSaveWhatsappTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (template: WhatsappTemplate) => {
      if (template.id) {
        const { data } = await api.put(`${API_ROUTES.whatsappTemplates}${template.id}/`, template);
        return data;
      }
      const { data } = await api.post(API_ROUTES.whatsappTemplates, template);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useDeleteWhatsappTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${API_ROUTES.whatsappTemplates}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useSyncWhatsappTemplates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(API_ROUTES.whatsappTemplatesSync);
      return data as WhatsappSyncResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useSubmitWhatsappTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      const { data } = await api.post(`${API_ROUTES.whatsappTemplates}${templateId}/submit/`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};
